import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/shared/api/ApiError';
import { IResetPasswordRequestDto, postResetPassword } from '@/shared/api/auth';
import { useAppMutation } from '@/shared/lib/useAppMutation';
import {
  TConfirmationProps,
  useGetConfirmation,
} from '@/shared/ui/Confirmations';
import { useNotifications } from '@/shared/ui/Notifications';
import { CustomTypography } from '@/shared/ui/Typography';
import { getRoutePath } from '@/shared/utils/routes';

const getConfirmationDescription = (t: (key: string) => string): ReactNode => (
  <>
    <CustomTypography component="span" variant="body1">
      {t('auth:resetPassword.theSamePasswordConfirmation.description.part1')}
    </CustomTypography>
    <CustomTypography component="span" variant="body1" sx={{ fontWeight: 600 }}>
      {t('auth:resetPassword.theSamePasswordConfirmation.description.part2')}
    </CustomTypography>
    <CustomTypography component="span" variant="body1">
      {t('auth:resetPassword.theSamePasswordConfirmation.description.part3')}
    </CustomTypography>
  </>
);

export const useResetPassword = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { getConfirmation } = useGetConfirmation();
  const { t } = useTranslation();

  const confirmationParams: TConfirmationProps = {
    title: t('auth:resetPassword.theSamePasswordConfirmation.title'),
    description: getConfirmationDescription(t),
    confirmText: t('auth:resetPassword.theSamePasswordConfirmation.confirmBtn'),
    rejectText: t('auth:resetPassword.theSamePasswordConfirmation.canselBtn'),
    priorityButton: 'reject',
    shouldCloseOnConfirm: true,
  };

  const resetPasswordMutation = useAppMutation({
    mutationFn: (vars: IResetPasswordRequestDto) => postResetPassword(vars),
    customError: true,
    onSuccess: () => {
      navigate(getRoutePath('AUTH', 'LOGIN'), { replace: true });
    },
  });

  const notifyError = (error: ApiError) => {
    addNotification({
      type: 'error',
      title: error.message,
      caption: error.data?.details,
    });
  };

  const resetPassword = async (data: IResetPasswordRequestDto) => {
    try {
      await resetPasswordMutation.mutateAsync(data);
      return;
    } catch (error) {
      const initialError = ApiError.fromError(error);

      if (initialError.status !== 406) {
        notifyError(initialError);
        return;
      }
    }

    const isConfirm = await getConfirmation(confirmationParams);
    if (!isConfirm) return;

    try {
      await resetPasswordMutation.mutateAsync({
        ...data,
        password_duplicate_agreement: true,
      });
    } catch (error) {
      notifyError(ApiError.fromError(error));
    }
  };

  return {
    ...resetPasswordMutation,
    resetPassword,
    isPending: resetPasswordMutation.isPending,
    isError: resetPasswordMutation.isError,
    error: resetPasswordMutation.error,
  };
};
